import {
  DEFAULT_TIME_ZONE,
  localDateIso,
  localHour,
  localTimeHm,
  zonedTimeToUtc,
} from '@mervonn/shared-types';

/**
 * Wall-clock helpers on a branch's IANA timezone, whatever the browser's own zone is.
 * `zone` is optional everywhere and defaults to the platform default (Africa/Nairobi);
 * components get the real one from `useDisplayZone`.
 */

/** HH:MM on the zone's clock. */
export function formatZoneTime(at: Date, zone: string = DEFAULT_TIME_ZONE): string {
  return localTimeHm(zone, at);
}

/** `YYYY-MM-DD` of an instant on the zone's calendar. */
export function zoneDateIso(at: Date, zone: string = DEFAULT_TIME_ZONE): string {
  return localDateIso(zone, at);
}

/** Hour of day, 0 to 23, on the zone's clock. */
export function zoneHourOf(at: Date, zone: string = DEFAULT_TIME_ZONE): number {
  return localHour(zone, at);
}

/** `YYYY-MM-DDTHH:mm` on the zone's clock, for a `datetime-local` input. */
export function zoneInputValue(at: Date, zone: string = DEFAULT_TIME_ZONE): string {
  return `${localDateIso(zone, at)}T${localTimeHm(zone, at)}`;
}

/** `YYYY-MM-DD` + `HH:MM` typed as the zone's wall clock, as an ISO instant, or null when invalid. */
export function zoneInstantIso(dateIso: string, hhmm: string, zone: string = DEFAULT_TIME_ZONE): string | null {
  const at = zonedTimeToUtc(zone, `${dateIso}T${hhmm}`);
  return Number.isNaN(at.getTime()) ? null : at.toISOString();
}

/** A calendar date plus whole days, `YYYY-MM-DD` in and out. */
export function addDaysIso(dateIso: string, days: number): string {
  const [y, m, d] = dateIso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}
